import { searchWords } from "./prop-listing.ts";
import type { StripPlays } from "./strips.ts";

/**
 * The frame and animation window's pure parts (ADR-107 decision 22, R-0185): one window, opened from every chooser
 * of a frame or a strip, lists what the chooser offers as large previews. Whatever opens it hands over plain items
 * (a still frame or a strip, with the words it is found by and the keys its art is drawn from), so nothing here
 * knows a manifest, a pack or a studio document. Here: what a search keeps, the sections, the one whole scale every
 * preview is drawn at, a strip's line of words, the frame the stage shows at a time, and the stepping keys.
 */

/**
 * One thing the window offers: its key among the items, its name, the heading it is listed under (its pack and
 * tileset, or a studio sprite's group; null for the ones nearest the opener, listed first under no heading), its
 * further search words, its size in frame pixels, the keys of the art it draws (one for a still, each frame of a
 * strip in order), each frame's milliseconds (none for a still), how a strip plays (null for a still) and, where
 * the chooser names them, each frame's own name.
 */
export interface WindowItem {
  readonly key: string;
  readonly name: string;
  readonly section: string | null;
  readonly words: readonly string[];
  readonly width: number;
  readonly height: number;
  readonly frames: readonly string[];
  readonly durations: readonly number[];
  readonly plays: StripPlays | null;
  /** Each frame's own name, where a chooser names the frames inside a strip, so one of them is found by it. */
  readonly frameNames?: readonly string[];
}

/**
 * Which items the window lists: every one, the stills alone, or the strips alone.
 */
export type WindowKind = "all" | "frames" | "animations";

/**
 * A run of items listed together under one heading.
 */
export interface WindowSection {
  readonly label: string | null;
  readonly items: readonly WindowItem[];
}

const SCALE_MIN = 2;
const SCALE_MAX = 8;
const TENTH_MS = 100;

/**
 * The texts an item is found by: its name, heading and search words as one, and each of its frames' names.
 *
 * @param item The item.
 * @returns The item's own text, then each frame's name, in lower case.
 */
function searchTexts(item: WindowItem): { readonly own: string; readonly frames: readonly string[] } {
  return { own: [item.name, item.section ?? "", ...item.words].join("\n").toLowerCase(), frames: (item.frameNames ?? []).map((name) => name.toLowerCase()) };
}

/**
 * The items a search keeps, in the order given: each word of the search must be in the item's name, its heading or
 * one of its search words, whatever the case, or every word in the name of one of its frames, so a frame inside a
 * strip is found by its own name; and the item must be of the kind listed.
 *
 * @param items The items offered.
 * @param search The text typed.
 * @param kind Which items are listed.
 * @returns The items kept; every one of the kind for an empty or blank search.
 */
export function searchWindow(items: readonly WindowItem[], search: string, kind: WindowKind): WindowItem[] {
  const words = searchWords(search);
  return items.filter((item) => {
    if ((kind === "frames" && item.plays !== null) || (kind === "animations" && item.plays === null)) {
      return false;
    }
    const texts = searchTexts(item);
    return words.every((word) => texts.own.includes(word)) || texts.frames.some((name) => words.every((word) => name.includes(word)));
  });
}

/**
 * The frame of a strip a search found by that frame's own name, which the window then opens the strip on: the
 * first frame whose name holds every word, when the strip's own name, heading and words do not answer the search.
 *
 * @param item The item.
 * @param search The text typed.
 * @returns The frame's index, or null for a blank search, an item found by its own words, or no frame found.
 */
export function searchedFrame(item: WindowItem, search: string): number | null {
  const words = searchWords(search);
  const texts = searchTexts(item);
  if (words.length === 0 || words.every((word) => texts.own.includes(word))) {
    return null;
  }
  const at = texts.frames.findIndex((name) => words.every((word) => name.includes(word)));
  return at < 0 ? null : at;
}

/**
 * The items the window draws of those a search kept: the first so many, and the chosen one after them when the cut
 * would leave it out, so what is chosen is always in sight; one the search dropped is not drawn.
 *
 * @param found The items the search kept.
 * @param chosen The chosen item, if any.
 * @param most The most previews drawn.
 * @returns The items drawn.
 */
export function drawnItems(found: readonly WindowItem[], chosen: WindowItem | undefined, most: number): WindowItem[] {
  const first = found.slice(0, most);
  return chosen !== undefined && found.includes(chosen) && !first.includes(chosen) ? [...first, chosen] : first;
}

/**
 * The items under their headings, in the order each heading first comes, the items with no heading first.
 *
 * @param items The items, in the order offered.
 * @returns The sections; none for no items.
 */
export function windowSections(items: readonly WindowItem[]): WindowSection[] {
  const byLabel = new Map<string | null, WindowItem[]>();
  for (const item of items) {
    if (item.section === null) {
      byLabel.set(null, [...(byLabel.get(null) ?? []), item]);
    }
  }
  for (const item of items) {
    if (item.section !== null) {
      byLabel.set(item.section, [...(byLabel.get(item.section) ?? []), item]);
    }
  }
  return [...byLabel].map(([label, held]) => ({ label, items: held }));
}

/**
 * The one whole scale every preview of the list is drawn at, so sizes compare by eye: the largest, up to 8, at
 * which the largest frame shown fits the tile, and never below 2, the least a large preview is worth. A frame too
 * large for the tile at 2 is drawn at 2 in a tile that grows to hold it.
 *
 * @param items The items shown.
 * @param tile The tile's side in CSS pixels.
 * @returns The scale; 2 for no items.
 */
export function windowScale(items: readonly WindowItem[], tile: number): number {
  if (items.length === 0) {
    return SCALE_MIN;
  }
  const largest = items.reduce((most, item) => Math.max(most, item.width, item.height), 1);
  return Math.max(SCALE_MIN, Math.min(SCALE_MAX, Math.floor(tile / largest)));
}

/**
 * A cycle in words: its frame count and its time, in seconds to one decimal from a tenth of a second up and in
 * milliseconds below ("8 frames, 1.3 s", "2 frames, 80 ms").
 *
 * @param durations Each frame's milliseconds.
 * @returns The words.
 */
export function cycleWords(durations: readonly number[]): string {
  const total = durations.reduce((sum, duration) => sum + duration, 0);
  const frames = durations.length === 1 ? "1 frame" : `${durations.length} frames`;
  const time = total >= TENTH_MS ? `${String(Math.round(total / TENTH_MS) / 10)} s` : `${total} ms`;
  return `${frames}, ${time}`;
}

/**
 * A strip's line under its name: how it plays, then its cycle.
 *
 * @param item The item.
 * @returns The line ("Loops, 8 frames, 0.8 s", "Plays once, 5 frames, 1.3 s"), or null for a still.
 */
export function stripLine(item: WindowItem): string | null {
  return item.plays === null ? null : `${item.plays === "loop" ? "Loops" : "Plays once"}, ${cycleWords(item.durations)}`;
}

/**
 * The frame the stage shows a given time after play began, at a speed: each frame for its own time, a loop starting
 * again after its last and never stopping, a once strip playing through and stopping on its last frame. A still
 * shows its one frame and never plays.
 *
 * @param item The item on the stage.
 * @param elapsedMs Milliseconds since play began.
 * @param speed How fast it plays: 1 is the strip's own durations, 0.5 half as fast.
 * @returns The index of the frame shown and whether it is still playing.
 * @throws RangeError when the time is negative or not finite, or the speed is not a positive finite number.
 */
export function stageFrameAt(item: WindowItem, elapsedMs: number, speed: number): { readonly index: number; readonly playing: boolean } {
  if (!Number.isFinite(elapsedMs) || elapsedMs < 0 || !Number.isFinite(speed) || speed <= 0) {
    throw new RangeError(`the stage shows a frame at a time at or after its start and a speed above 0, not ${elapsedMs} ms at ${speed}`);
  }
  if (item.plays === null || item.durations.length === 0) {
    return { index: 0, playing: false };
  }
  const period = item.durations.reduce((sum, duration) => sum + duration, 0);
  const at = elapsedMs * speed;
  if (item.plays === "once" && at >= period) {
    return { index: item.durations.length - 1, playing: false };
  }
  let within = at % period;
  for (let index = 0; index < item.durations.length; index += 1) {
    within -= item.durations[index] as number;
    if (within < 0) {
      return { index, playing: true };
    }
  }
  return { index: item.durations.length - 1, playing: true };
}

/**
 * Where a stepping key moves the stage's frame: Left and Right one frame, stopping at the ends, Home and End to the
 * first and last.
 *
 * @param index The frame shown.
 * @param count How many frames the item has.
 * @param key The key pressed, as a key event names it.
 * @returns The frame to show, or null for any other key or an item of fewer than two frames.
 */
export function stepFrame(index: number, count: number, key: string): number | null {
  if (count < 2) {
    return null;
  }
  switch (key) {
    case "ArrowLeft":
      return Math.max(0, index - 1);
    case "ArrowRight":
      return Math.min(count - 1, index + 1);
    case "Home":
      return 0;
    case "End":
      return count - 1;
    default:
      return null;
  }
}
